import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import {
  deleteFormularioById,
  deleteFormularioOfEmpresaById,
  toggleStatus,
  toggleStatusOfEmpresaFormulario,
} from '../../core/_requests'
import { duplicarFormulario } from '../../../formularios-create/core/_requests'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  IconPaths,
  Permission,
  showAlertLoading,
  showConfirmAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { RowActions, useToast } from '../../../../../../components'
import { FormularioDtoUpdateSituacao, FormularioList } from '../../core/_models'
import { useAuth } from '../../../../../auth'

type Props = {
  formulario: FormularioList
}

export const FormularioActionsCell: FC<Props> = ({ formulario }) => {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { refetch } = useQueryResponse()
  const { currentUser } = useAuth()
  const { t } = useT()
  const nome = formulario.nome || 'Formulário'

  const isAdmin = currentUser?.grupo?.nome === Permission.Admin

  const editForm = () =>
    navigate(
      `/apps/formularios/editar?id=${formulario.id}${
        formulario.empresa ? `&empresaId=${formulario.empresa?.id}` : ''
      }`
    )

  const setStatus = async (ativo: boolean) => {
    try {
      showAlertLoading()
      const data: FormularioDtoUpdateSituacao = { id: Number(formulario.id), ativo }
      isAdmin && formulario.empresa
        ? await toggleStatusOfEmpresaFormulario(formulario.empresa.id, data)
        : await toggleStatus(data)
      Swal.close()
      showToast({ variant: 'success', title: `${nome} foi ${ativo ? 'ativado' : 'inativado'}.` })
      refetch()
    } catch (error) {
      showError(error)
    }
  }

  const handleDuplicar = async () => {
    const isConfirmed = await showConfirmAlert({
      text: t('formularios.bloqueio.duplicar.confirmacao', { nome }),
      confirmButtonText: t('common.yes'),
    })
    if (!isConfirmed) return
    try {
      showAlertLoading()
      const copia = await duplicarFormulario(formulario.id)
      Swal.close()
      navigate(
        `/apps/formularios/editar?id=${copia.id}${
          copia.empresa ? `&empresaId=${copia.empresa.id}` : ''
        }`
      )
    } catch (error) {
      showError(error)
    }
  }

  // Inativar pede confirmação explicando a consequência; ativar é reversível e não pede.
  const askInativar = async () => {
    const confirmed = await showConfirmAlert()
    if (confirmed) await setStatus(false)
  }

  // Excluir é destrutivo: confirmação com a consequência.
  const askDelete = async () => {
    const confirmed = await showConfirmAlert()
    if (!confirmed) return

    try {
      showAlertLoading()
      isAdmin
        ? await deleteFormularioOfEmpresaById(Number(formulario.id))
        : await deleteFormularioById(formulario.id)
      Swal.close()
      showToast({ variant: 'success', title: `${nome} foi excluído.` })
      refetch()
    } catch (error) {
      showError(error)
    }
  }

  return (
    <RowActions
      itemName={nome}
      primaryAction={{ label: 'Editar', onClick: editForm, icon: IconPaths.Pen }}
      items={[
        {
          label: 'Vínculos por especialidade',
          onClick: () => navigate(`/apps/formularios/vinculos/especialidade/${formulario.id}`),
        },
        {
          label: 'Vínculos por tipo de atendimento',
          onClick: () => navigate(`/apps/formularios/vinculos/tipoAtendimento/${formulario.id}`),
        },
        { label: t('formularios.bloqueio.duplicar.acao'), onClick: handleDuplicar },
        formulario.ativo
          ? { label: 'Inativar', onClick: askInativar, divider: true }
          : { label: 'Ativar', onClick: () => setStatus(true), divider: true },
        { label: 'Excluir', onClick: askDelete, danger: true },
      ]}
    />
  )
}
